import { useCallback, useEffect, useRef, useState } from 'react';

import { ErrorBoundary } from 'core/Components';
import { Box, Flex, Stack, Text, UnstyledButton } from 'ui';

import { RecentDonations } from '../../components/Causes/RecentDonations/RecentDonations';
import classes from './About.module.css';
import { useCausePageContext } from './CauseContext';
import { AboutLinks } from './components/AboutLinks/AboutLinks';
import { AboutMemberships } from './components/AboutMemberships/AboutMemberships';
import { AboutProducts } from './components/AboutProducts/AboutProducts';
import { AboutStudentFeesCard } from './components/AboutStudentFeesCard/AboutStudentFeesCard';

const useTextClamping = ({ isExpanded }: { isExpanded: boolean }) => {
  const [isClamped, setIsClamped] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const nodeRef = useRef<HTMLDivElement | null>(null);

  const textRef = useCallback((node: HTMLDivElement | null) => {
    nodeRef.current = node;
  }, []);

  useEffect(() => {
    const node = nodeRef.current;

    // Always clean up first
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    // Only set up observer if we have a node and it's not expanded
    if (node && !isExpanded) {
      const checkClamping = () => {
        const { scrollHeight, clientHeight } = node;
        setIsClamped(scrollHeight > clientHeight);
      };

      checkClamping();

      resizeObserverRef.current = new ResizeObserver(checkClamping);
      resizeObserverRef.current.observe(node);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [isExpanded]);

  return { isClamped, textRef };
};

const AboutDescription = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { cause } = useCausePageContext();
  const { isClamped, textRef } = useTextClamping({ isExpanded });

  const showReadMoreButton = isClamped || isExpanded;
  const showGradientOverlay = isClamped && !isExpanded;

  if (!cause?.description) return null;

  return (
    <Box className={classes.AboutDescription}>
      <Text
        ref={textRef}
        lineClamp={isExpanded ? undefined : 6}
        className={classes.AboutDescription__text}
      >
        {cause.description}
      </Text>

      {showGradientOverlay && <Box className={classes.AboutDescription__gradient} />}

      {showReadMoreButton && (
        <UnstyledButton
          c="cobalt"
          onClick={() => setIsExpanded(!isExpanded)}
          fw="500"
          className={classes.AboutDescription__button}
          data-expanded={isExpanded || undefined}
        >
          {isExpanded ? 'Read Less' : 'Read More'}
        </UnstyledButton>
      )}
    </Box>
  );
};

const About = () => {
  const { cause, showStoreTab } = useCausePageContext();

  return (
    <Flex direction="column" gap={{ base: 'lg', sm: 'xl' }}>
      <Stack>
        <AboutDescription />
        <AboutLinks />
      </Stack>

      <ErrorBoundary.Card>
        <AboutStudentFeesCard />
      </ErrorBoundary.Card>

      {showStoreTab && (
        <>
          <AboutMemberships />
          <AboutProducts />
        </>
      )}
      <RecentDonations causeId={cause.uuid} />
    </Flex>
  );
};

export { About };
